import React, { useState, useMemo, useEffect, useRef } from "react";
import { 
  Copy, BookOpen,
  ChevronUp, ChevronDown, Download, Columns, ToggleLeft, ToggleRight, Star
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { extractPmidFromUrl, getPaperStableId } from "../../../shared/utils/paper";
import {
  DEFAULT_PAPER_SORT_MODE,
  PAPER_SORT_MODES,
} from "../utils/paperSorting";

const MAX_CELL_HEIGHT_PX = 146;

const buildDefaultSortConfig = (sortMode) => {
  if (sortMode === PAPER_SORT_MODES.YEAR) {
    return [
      { key: "year", direction: "desc" },
      { key: "relevance_score", direction: "desc" },
    ];
  }
  if (sortMode === PAPER_SORT_MODES.CITATIONS) {
    return [
      { key: "cited_by", direction: "desc" },
      { key: "relevance_score", direction: "desc" },
    ];
  }
  return [{ key: "relevance_score", direction: "desc" }];
};

function CollapsibleCell({
  children,
  isExpanded,
  onToggle,
  showMoreLabel,
  showLessLabel
}) {
  const contentRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    const checkOverflow = () => {
      setIsOverflowing(element.scrollHeight > MAX_CELL_HEIGHT_PX + 1);
    };

    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(element);

    return () => observer.disconnect();
  }, [children]);

  return (
    <div>
      <div
        ref={contentRef}
        className="table-cell-clamp"
        style={
          isExpanded
            ? undefined
            : { maxHeight: `${MAX_CELL_HEIGHT_PX}px`, overflow: "hidden" }
        }
      >
        {children}
      </div>
      {isOverflowing && (
        <button
          type="button"
          onClick={onToggle}
          className="mt-2 text-[11px] font-semibold text-violet-600 hover:text-violet-700"
        >
          {isExpanded ? showLessLabel : showMoreLabel}
        </button>
      )}
    </div>
  );
}

export default function PapersTable({ 
  papers, 
  selectedPaperIds, 
  setSelectedPaperIds, 
  highlightedPaperId,
  setSelectedImage,
  togglePaperSelection,
  onToggleContextPaper,
  isContextPaperSelected,
  canAddContextPaper,
  highlightedPapers,
  onToggleBookmark,
  currentSearchId,
  onTrackPaperEvent,
  onTrackCsvExport,
  registerImpressionTarget,
  sortMode = DEFAULT_PAPER_SORT_MODE,
  sourceSelectionNudgeKey,
  sourceSelectionNudgePaperId,
}) {
  const { t } = useTranslation();
  const [tableQuery, setTableQuery] = useState("");
  const [expandedCells, setExpandedCells] = useState(new Set());
  const [visibleColumns, setVisibleColumns] = useState(new Set([
    "select", "rank_score", "title", "year", "images", "journal", 
    "authors", "citations", "research_question", "design", "population",
    "exposure_intervention", "endpoint", "statistics", "key_results", "rationale"
  ]));
  const [sortConfig, setSortConfig] = useState(() => buildDefaultSortConfig(sortMode));
  const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);

  useEffect(() => {
    setSortConfig(buildDefaultSortConfig(sortMode));
  }, [sortMode]);

  const columnDefs = [
    { key: "rank_score", label: t("dashboard.table.rankScore"), sortable: true, id: "relevance_score" },
    { key: "title", label: t("dashboard.table.title"), sortable: true, id: "title" },
    { key: "year", label: t("dashboard.table.year"), sortable: true, id: "year" },
    { key: "images", label: t("dashboard.table.images"), sortable: false },
    { key: "journal", label: t("dashboard.table.journal"), sortable: true, id: "journal" },
    { key: "authors", label: t("dashboard.table.authors"), sortable: false },
    { key: "citations", label: t("dashboard.table.citations"), sortable: true, id: "cited_by" },
    { key: "research_question", label: t("dashboard.table.researchQuestion"), sortable: false },
    { key: "design", label: t("dashboard.table.design"), sortable: false },
    { key: "population", label: t("dashboard.table.population"), sortable: false },
    { key: "exposure_intervention", label: t("dashboard.table.exposureIntervention"), sortable: false },
    { key: "endpoint", label: t("dashboard.table.endpoint"), sortable: false },
    { key: "statistics", label: t("dashboard.table.statistics"), sortable: false },
    { key: "key_results", label: t("dashboard.table.keyResults"), sortable: false },
    { key: "rationale", label: t("dashboard.table.rationale"), sortable: false },
  ];

  const toggleCellExpansion = (cellKey) => {
    setExpandedCells((prev) => {
      const next = new Set(prev);
      if (next.has(cellKey)) {
        next.delete(cellKey);
      } else {
        next.add(cellKey);
      }
      return next;
    });
  };

  const handleCopySelectedPmids = async () => {
     const selected = papers.filter((p, index) => {
       const pmid = p.pmid || extractPmidFromUrl(p.url);
       const paperId = getPaperStableId(p, index);
       return selectedPaperIds.has(paperId) || selectedPaperIds.has(pmid);
     });
     
     const pmids = selected.map(p => p.pmid || extractPmidFromUrl(p.url)).filter(Boolean).join("\n");
     if (pmids) {
       await navigator.clipboard.writeText(pmids);
       alert(t("dashboard.table.pmidsCopied", { count: selected.length }));
     }
   };

   const handleExportCSV = () => {
     const selected = papers.filter((p, index) => {
       const pmid = p.pmid || extractPmidFromUrl(p.url);
       const paperId = getPaperStableId(p, index);
       return selectedPaperIds.has(paperId) || selectedPaperIds.has(pmid);
     });
     const dataToExport = selected.length > 0 ? selected : papers;
     onTrackCsvExport?.({
       surface: "results_table",
       exportScope: selected.length > 0 ? "selected" : "all",
       exportCount: dataToExport.length,
       highlightedOnly: false,
       metadata: { selected_count: selected.length, total_count: papers.length },
     });
 
     const headers = [
       "PMID",
       "Title",
       "Year",
       "Journal",
       "Authors",
       "Citations",
       "Score",
       "Research Question",
       "Design",
       "Population",
       "Exposure / Intervention",
       "Endpoint",
       "Statistics",
       "Key Results",
       "Rationale",
       "Images",
       "Full Text",
       "URL"
     ];
 
     const csvContent = "\uFEFF" + [
       headers.join(","),
       ...dataToExport.map(p => [
         p.pmid || extractPmidFromUrl(p.url) || "",
         `"${(p.title || "").replace(/"/g, '""')}"`,
         p.year || p.pub_year || "",
         `"${(p.journal || "").replace(/"/g, '""')}"`,
         `"${(p.authors || []).join("; ").replace(/"/g, '""')}"`,
         p.cited_by || 0,
         p.relevance_score || 0,
         `"${(p.research_question || "").replace(/"/g, '""')}"`,
         `"${(p.design || "").replace(/"/g, '""')}"`,
         `"${(p.population || "").replace(/"/g, '""')}"`,
         `"${(p.exposure_intervention || "").replace(/"/g, '""')}"`,
         `"${(p.endpoint || "").replace(/"/g, '""')}"`,
         `"${(p.statistics || "").replace(/"/g, '""')}"`,
         `"${(p.key_results || "").replace(/"/g, '""').replace(/\n/g, " ")}"`,
         `"${(p.rationale || "").replace(/"/g, '""').replace(/\n/g, " ")}"`,
         `"${(p.image_urls || []).join("; ").replace(/"/g, '""')}"`,
         p.pmcid ? `https://pmc.ncbi.nlm.nih.gov/articles/${p.pmcid}/` : "",
         p.url || ""
       ].join(","))
     ].join("\n");
 
     const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
     const link = document.createElement("a");
     link.href = URL.createObjectURL(blob);
     link.download = `pubmed_papers_${new Date().toISOString().slice(0, 10)}.csv`;
     link.click();
   };

  const handleSort = (key, event) => {
    setSortConfig(prev => {
      const existing = prev.find(s => s.key === key);
      let newConfig;
      
      if (event.shiftKey) {
        if (existing) {
          newConfig = prev.map(s => s.key === key ? { ...s, direction: s.direction === "asc" ? "desc" : "asc" } : s);
        } else {
          newConfig = [...prev, { key, direction: "desc" }];
        }
      } else {
        if (existing && prev[0].key === key) {
          newConfig = [{ key, direction: existing.direction === "asc" ? "desc" : "asc" }];
        } else {
          newConfig = [{ key, direction: "desc" }];
        }
      }
      return newConfig;
    });
  };

  const sortedPapers = useMemo(() => {
    let sortableItems = [...papers];
    if (sortConfig.length > 0) {
      sortableItems.sort((a, b) => {
        for (const { key, direction } of sortConfig) {
          let aValue = a[key];
          let bValue = b[key];
          
          if (key === "year") {
            aValue = parseInt(a.year || a.pub_year || 0);
            bValue = parseInt(b.year || b.pub_year || 0);
          } else if (key === "relevance_score") {
            aValue = parseFloat(a.relevance_score || 0);
            bValue = parseFloat(b.relevance_score || 0);
          } else if (key === "cited_by") {
            aValue = parseInt(a.cited_by || 0);
            bValue = parseInt(b.cited_by || 0);
          }

          if (aValue < bValue) return direction === "asc" ? -1 : 1;
          if (aValue > bValue) return direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [papers, sortConfig]);

  const filteredPapers = useMemo(() => {
    if (!tableQuery.trim()) return sortedPapers;
    const q = tableQuery.toLowerCase();
    return sortedPapers.filter(p => {
      const title = (p.title || "").toLowerCase();
      const authors = (p.authors || []).join(" ").toLowerCase();
      const researchQuestion = (p.research_question || "").toLowerCase();
      const keyResults = (p.key_results || "").toLowerCase();
      const statistics = (p.statistics || "").toLowerCase();
      const rationale = (p.rationale || "").toLowerCase();
      const keywords = (p.keywords || []).join(" ").toLowerCase();
      return (
        title.includes(q) ||
        authors.includes(q) ||
        researchQuestion.includes(q) ||
        keyResults.includes(q) ||
        statistics.includes(q) ||
        rationale.includes(q) ||
        keywords.includes(q)
      );
    });
  }, [sortedPapers, tableQuery]);

  const scoreRankMap = useMemo(() => {
    const ranked = [...papers].sort((a, b) => {
      const aScore = parseFloat(a.relevance_score || 0);
      const bScore = parseFloat(b.relevance_score || 0);
      if (aScore < bScore) return 1;
      if (aScore > bScore) return -1;
      return 0;
    });
    const map = new Map();
    ranked.forEach((paper, index) => {
      const paperId = getPaperStableId(paper, index);
      map.set(paperId, index + 1);
    });
    return map;
  }, [papers]);

  const toggleSelectionAll = (checked) => {
    if (checked) {
      setSelectedPaperIds(
        new Set(
          papers
            .map((paper, index) => getPaperStableId(paper, index))
            .filter(Boolean),
        ),
      );
    } else {
      setSelectedPaperIds(new Set());
    }
  };

  return (
    <div className="results-right">
      <div className="table-shell">
        <div className="table-controls">
          <div className="flex items-center gap-4">
            <div className="table-meta">
              <span className="table-count">
                {tableQuery ? `${filteredPapers.length} / ${papers.length}` : papers.length} {t("dashboard.table.papers")}
              </span>
              <span className="table-note hidden sm:inline">
                {t("dashboard.table.scrollNote")}
              </span>
            </div>
            
             {selectedPaperIds.size > 0 && (
               <div className="flex items-center gap-2 animate-fadeIn">
                 <div className="h-4 w-px bg-slate-200 mx-2"></div>
                 <button 
                   onClick={handleExportCSV}
                   className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
                   title={t("dashboard.table.exportCsv")}
                 >
                   <Download className="w-3.5 h-3.5" />
                   <span className="hidden sm:inline">{t("dashboard.table.csv")}</span>
                 </button>
                 <button 
                   onClick={handleCopySelectedPmids}
                   className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
                   title={t("dashboard.table.copyPmids")}
                 >
                   <Copy className="w-3.5 h-3.5" />
                   <span className="hidden sm:inline">{t("dashboard.table.pmids")}</span>
                 </button>
               </div>
             )}
          </div>

           <div className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="text"
                  value={tableQuery}
                  onChange={(e) => setTableQuery(e.target.value)}
                  placeholder={t("dashboard.table.search")}
                  className="h-8 w-48 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 placeholder-slate-400 focus:outline-none"
                />
              </div>
              <div className="relative">
                <button 
                  onClick={() => setIsColumnMenuOpen(!isColumnMenuOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors border border-transparent hover:border-slate-200"
                >
                  <Columns className="w-4 h-4" />
                  <span>{t("dashboard.table.columnsCustomize")}</span>
                </button>
                {isColumnMenuOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setIsColumnMenuOpen(false)}
                    />
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-100 z-50 py-2 animate-fadeIn">
                      <div className="px-3 py-2 border-b border-slate-50 mb-1">
                        <h3 className="text-xs font-semibold text-slate-900">{t("dashboard.table.visibleColumns")}</h3>
                      </div>
                      <div className="max-h-[300px] overflow-y-auto px-1">
                        {columnDefs.map(col => (
                          <label key={col.key} className="flex items-center px-3 py-2 hover:bg-slate-50 rounded cursor-pointer group">
                            <input
                              type="checkbox"
                              checked={visibleColumns.has(col.key)}
                              onChange={() => {
                                const newSet = new Set(visibleColumns);
                                if (newSet.has(col.key)) newSet.delete(col.key);
                                else newSet.add(col.key);
                                setVisibleColumns(newSet);
                              }}
                              className="w-3.5 h-3.5 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                            />
                            <span className="ml-2 text-xs text-slate-600 group-hover:text-slate-900">{col.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        
        <div className="table-scroll">
          <table className="papers-table">
            <thead>
              <tr>
                <th className="table-select sticky-col-1">
                  <input
                    type="checkbox"
                    checked={selectedPaperIds.size === papers.length && papers.length > 0}
                    onChange={(event) => toggleSelectionAll(event.target.checked)}
                  />
                </th>
                {visibleColumns.has("rank_score") && (
                  <th 
                    className="table-rank-score sticky-col-2 cursor-pointer hover:bg-slate-50 transition-colors group"
                    onClick={(e) => handleSort("relevance_score", e)}
                  >
                    <div className="flex items-center gap-1">
                      {t("dashboard.table.rankScore")}
                      {sortConfig.find(s => s.key === "relevance_score") ? (
                        sortConfig.find(s => s.key === "relevance_score").direction === "asc" ? 
                          <ChevronUp className="w-3 h-3 text-violet-600" /> : 
                          <ChevronDown className="w-3 h-3 text-violet-600" />
                      ) : (
                        <div className="flex flex-col opacity-0 group-hover:opacity-30">
                          <ChevronUp className="w-2 h-2" />
                          <ChevronDown className="w-2 h-2" />
                        </div>
                      )}
                    </div>
                  </th>
                )}
                {visibleColumns.has("title") && (
                   <th onClick={(e) => handleSort("title", e)} className="sticky-col-3 cursor-pointer hover:bg-slate-50 group">
                    <div className="flex items-center gap-1">
                      {t("dashboard.table.title")}
                      {sortConfig.find(s => s.key === "title") && (
                        sortConfig.find(s => s.key === "title").direction === "asc" ? <ChevronUp className="w-3 h-3 text-violet-600" /> : <ChevronDown className="w-3 h-3 text-violet-600" />
                      )}
                    </div>
                  </th>
                )}
                {visibleColumns.has("year") && (
                  <th onClick={(e) => handleSort("year", e)} className="cursor-pointer hover:bg-slate-50">
                     <div className="flex items-center gap-1">
                      {t("dashboard.table.year")}
                      {sortConfig.find(s => s.key === "year") && (
                        sortConfig.find(s => s.key === "year").direction === "asc" ? <ChevronUp className="w-3 h-3 text-violet-600" /> : <ChevronDown className="w-3 h-3 text-violet-600" />
                      )}
                    </div>
                  </th>
                )}
                {visibleColumns.has("images") && <th>{t("dashboard.table.images")}</th>}
                {visibleColumns.has("journal") && (
                  <th onClick={(e) => handleSort("journal", e)} className="cursor-pointer hover:bg-slate-50">
                    <div className="flex items-center gap-1">
                      {t("dashboard.table.journal")}
                      {sortConfig.find(s => s.key === "journal") && (
                         sortConfig.find(s => s.key === "journal").direction === "asc" ? <ChevronUp className="w-3 h-3 text-violet-600" /> : <ChevronDown className="w-3 h-3 text-violet-600" />
                      )}
                    </div>
                  </th>
                )}
                {visibleColumns.has("authors") && <th>{t("dashboard.table.authors")}</th>}
                {visibleColumns.has("citations") && (
                  <th onClick={(e) => handleSort("cited_by", e)} className="cursor-pointer hover:bg-slate-50">
                    <div className="flex items-center gap-1">
                      {t("dashboard.table.citations")}
                      {sortConfig.find(s => s.key === "cited_by") && (
                         sortConfig.find(s => s.key === "cited_by").direction === "asc" ? <ChevronUp className="w-3 h-3 text-violet-600" /> : <ChevronDown className="w-3 h-3 text-violet-600" />
                      )}
                    </div>
                  </th>
                )}
                {visibleColumns.has("research_question") && <th>{t("dashboard.table.researchQuestion")}</th>}
                {visibleColumns.has("design") && <th>{t("dashboard.table.design")}</th>}
                {visibleColumns.has("population") && <th>{t("dashboard.table.population")}</th>}
                {visibleColumns.has("exposure_intervention") && <th>{t("dashboard.table.exposureIntervention")}</th>}
                {visibleColumns.has("endpoint") && <th>{t("dashboard.table.endpoint")}</th>}
                {visibleColumns.has("statistics") && <th>{t("dashboard.table.statistics")}</th>}
                {visibleColumns.has("key_results") && <th>{t("dashboard.table.keyResults")}</th>}
                {visibleColumns.has("rationale") && <th>{t("dashboard.table.rationale")}</th>}
              </tr>
            </thead>
            <tbody>
              {filteredPapers.map((paper, idx) => {
                const paperId = getPaperStableId(paper, idx);
                const pmid = paper.pmid || extractPmidFromUrl(paper.url);
                const isBookmarked = pmid ? highlightedPapers?.has(pmid) : false;
                const canBookmark = Boolean(pmid && currentSearchId && onToggleBookmark);
                const isContextSelected = Boolean(isContextPaperSelected?.(paper));
                const canToggleContext =
                  isContextSelected || !canAddContextPaper || canAddContextPaper(paper);
                const shouldShowSourceNudge =
                  Boolean(sourceSelectionNudgeKey) &&
                  Boolean(sourceSelectionNudgePaperId) &&
                  paperId === sourceSelectionNudgePaperId &&
                  !isContextSelected;
                const showMoreLabel = t("dashboard.table.showMore");
                const showLessLabel = t("dashboard.table.showLess");
                return (
                  <tr 
                    key={paperId} 
                    id={`paper-row-${paperId}`}
                    data-paper-card-id={paperId || undefined}
                    ref={registerImpressionTarget?.(paper, idx, "results_table")}
                    className={`${
                      highlightedPaperId === paperId
                        ? "bg-yellow-50 transition-colors duration-500"
                        : ""
                    } ${shouldShowSourceNudge ? "paper-source-nudge-row" : ""}`}
                  >
                    <td className="table-select sticky-col-1">
                      <CollapsibleCell
                        isExpanded={expandedCells.has(`${paperId}:select`)}
                        onToggle={() => toggleCellExpansion(`${paperId}:select`)}
                        showMoreLabel={showMoreLabel}
                        showLessLabel={showLessLabel}
                      >
                        <input
                          type="checkbox"
                          checked={selectedPaperIds.has(paperId)}
                          onChange={() => togglePaperSelection(paperId)}
                        />
                      </CollapsibleCell>
                    </td>
                    {visibleColumns.has("rank_score") && (
                      <td className="table-rank-score sticky-col-2">
                        <CollapsibleCell
                          isExpanded={expandedCells.has(`${paperId}:rank_score`)}
                          onToggle={() => toggleCellExpansion(`${paperId}:rank_score`)}
                          showMoreLabel={showMoreLabel}
                          showLessLabel={showLessLabel}
                        >
                          <div className="flex flex-col leading-tight gap-2">
                             <span className="table-rank">{scoreRankMap.get(paperId) || idx + 1}</span>
                             <span className="table-score">{(parseFloat(paper.relevance_score) || 0).toFixed(1)}</span>
                          </div>
                        </CollapsibleCell>
                      </td>
                    )}
                    {visibleColumns.has("title") && (
                      <td className="table-title sticky-col-3">
                        <CollapsibleCell
                          isExpanded={expandedCells.has(`${paperId}:title`)}
                          onToggle={() => toggleCellExpansion(`${paperId}:title`)}
                          showMoreLabel={showMoreLabel}
                          showLessLabel={showLessLabel}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              {paper.url ? (
                                <a
                                  href={paper.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() =>
                                    onTrackPaperEvent?.(
                                      "paper_title_click",
                                      paper,
                                      idx,
                                      "results_table",
                                    )
                                  }
                                >
                                  {paper.title}
                                </a>
                              ) : paper.title}
                              {paper.pmcid && (
                                <div className="mt-4">
                                  <a
                                    href={`https://pmc.ncbi.nlm.nih.gov/articles/${paper.pmcid}/`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="table-link flex items-center gap-1"
                                    onClick={() =>
                                      onTrackPaperEvent?.(
                                        "paper_full_text_click",
                                        paper,
                                        idx,
                                        "results_table",
                                      )
                                    }
                                  >
                                    <BookOpen className="h-3 w-3" />
                                    {t("dashboard.table.fullText")}
                                  </a>
                                </div>
                              )}
                            </div>
                            <div className="flex shrink-0 items-center gap-1">
                              {onToggleContextPaper && (
                                <button
                                  key={
                                    shouldShowSourceNudge
                                      ? `source-selection-nudge-${sourceSelectionNudgeKey}`
                                      : "source-selection-default"
                                  }
                                  type="button"
                                  onClick={() => onToggleContextPaper(paper)}
                                  disabled={!canToggleContext}
                                  data-context-button="true"
                                  className={`relative p-0.5 rounded-md transition-colors ${
                                    isContextSelected
                                      ? "bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-700"
                                      : canToggleContext
                                        ? "text-slate-400 hover:bg-blue-50 hover:text-blue-700"
                                      : "cursor-not-allowed text-slate-300"
                                  } ${
                                    shouldShowSourceNudge ? "paper-source-nudge-button" : ""
                                  }`}
                                  title={
                                    isContextSelected
                                      ? t("dashboard.card.removeFromSources")
                                      : canToggleContext
                                        ? t("dashboard.table.addToContext")
                                      : t("paperAnalysis.sourceLimitReached")
                                  }
                                >
                                  {isContextSelected ? (
                                    <ToggleRight className="w-6 h-6" />
                                  ) : (
                                    <ToggleLeft className="w-6 h-6" />
                                  )}
                                </button>
                              )}
                              <button
                                onClick={() => onToggleBookmark && onToggleBookmark(paper)}
                                disabled={!canBookmark}
                                className={`p-1 rounded-md transition-colors ${
                                  isBookmarked
                                    ? "bg-amber-100 text-amber-600 hover:bg-amber-200"
                                    : "text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                                } ${!canBookmark ? "opacity-50 cursor-not-allowed" : ""}`}
                                title={
                                  isBookmarked
                                    ? t("dashboard.card.removeBookmark")
                                    : t("dashboard.card.bookmark")
                                }
                              >
                                <Star className={`w-3.5 h-3.5 ${isBookmarked ? "fill-current" : ""}`} />
                              </button>
                            </div>
                          </div>
                        </CollapsibleCell>
                      </td>
                    )}
                    {visibleColumns.has("year") && (
                      <td className="table-year">
                        <CollapsibleCell
                          isExpanded={expandedCells.has(`${paperId}:year`)}
                          onToggle={() => toggleCellExpansion(`${paperId}:year`)}
                          showMoreLabel={showMoreLabel}
                          showLessLabel={showLessLabel}
                        >
                          {paper.year || paper.pub_year}
                        </CollapsibleCell>
                      </td>
                    )}
                    {visibleColumns.has("images") && (
                      <td className="table-images-cell">
                        <CollapsibleCell
                          isExpanded={expandedCells.has(`${paperId}:images`)}
                          onToggle={() => toggleCellExpansion(`${paperId}:images`)}
                          showMoreLabel={showMoreLabel}
                          showLessLabel={showLessLabel}
                        >
                          {paper.image_urls && paper.image_urls.length > 0 ? (
                            <div className="table-images">
                              {paper.image_urls.map((url, i) => (
                                <div 
                                  key={i} 
                                  className="table-image cursor-pointer hover:border-blue-500"
                                  onClick={() => setSelectedImage(url)}
                                >
                                  <img src={url} alt={`Figure ${i + 1}`} loading="lazy" />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="table-muted">-</span>
                          )}
                        </CollapsibleCell>
                      </td>
                    )}
                    {visibleColumns.has("journal") && (
                      <td className="table-muted">
                        <CollapsibleCell
                          isExpanded={expandedCells.has(`${paperId}:journal`)}
                          onToggle={() => toggleCellExpansion(`${paperId}:journal`)}
                          showMoreLabel={showMoreLabel}
                          showLessLabel={showLessLabel}
                        >
                          {paper.journal}
                        </CollapsibleCell>
                      </td>
                    )}
                    {visibleColumns.has("authors") && (
                      <td className="table-muted">
                        <CollapsibleCell
                          isExpanded={expandedCells.has(`${paperId}:authors`)}
                          onToggle={() => toggleCellExpansion(`${paperId}:authors`)}
                          showMoreLabel={showMoreLabel}
                          showLessLabel={showLessLabel}
                        >
                          {paper.authors?.join(", ") || ""}
                        </CollapsibleCell>
                      </td>
                    )}
                    {visibleColumns.has("citations") && (
                      <td className="table-citations">
                        <CollapsibleCell
                          isExpanded={expandedCells.has(`${paperId}:citations`)}
                          onToggle={() => toggleCellExpansion(`${paperId}:citations`)}
                          showMoreLabel={showMoreLabel}
                          showLessLabel={showLessLabel}
                        >
                          {paper.cited_by || 0}
                        </CollapsibleCell>
                      </td>
                    )}
                    {visibleColumns.has("research_question") && (
                      <td>
                        <CollapsibleCell
                          isExpanded={expandedCells.has(`${paperId}:research_question`)}
                          onToggle={() => toggleCellExpansion(`${paperId}:research_question`)}
                          showMoreLabel={showMoreLabel}
                          showLessLabel={showLessLabel}
                        >
                          {paper.research_question}
                        </CollapsibleCell>
                      </td>
                    )}
                    {visibleColumns.has("design") && (
                      <td>
                        <CollapsibleCell
                          isExpanded={expandedCells.has(`${paperId}:design`)}
                          onToggle={() => toggleCellExpansion(`${paperId}:design`)}
                          showMoreLabel={showMoreLabel}
                          showLessLabel={showLessLabel}
                        >
                          {paper.design}
                        </CollapsibleCell>
                      </td>
                    )}
                    {visibleColumns.has("population") && (
                      <td>
                        <CollapsibleCell
                          isExpanded={expandedCells.has(`${paperId}:population`)}
                          onToggle={() => toggleCellExpansion(`${paperId}:population`)}
                          showMoreLabel={showMoreLabel}
                          showLessLabel={showLessLabel}
                        >
                          {paper.population}
                        </CollapsibleCell>
                      </td>
                    )}
                    {visibleColumns.has("exposure_intervention") && (
                      <td>
                        <CollapsibleCell
                          isExpanded={expandedCells.has(`${paperId}:exposure_intervention`)}
                          onToggle={() => toggleCellExpansion(`${paperId}:exposure_intervention`)}
                          showMoreLabel={showMoreLabel}
                          showLessLabel={showLessLabel}
                        >
                          {paper.exposure_intervention}
                        </CollapsibleCell>
                      </td>
                    )}
                    {visibleColumns.has("endpoint") && (
                      <td>
                        <CollapsibleCell
                          isExpanded={expandedCells.has(`${paperId}:endpoint`)}
                          onToggle={() => toggleCellExpansion(`${paperId}:endpoint`)}
                          showMoreLabel={showMoreLabel}
                          showLessLabel={showLessLabel}
                        >
                          {paper.endpoint || "-"}
                        </CollapsibleCell>
                      </td>
                    )}
                    {visibleColumns.has("statistics") && (
                      <td>
                        <CollapsibleCell
                          isExpanded={expandedCells.has(`${paperId}:statistics`)}
                          onToggle={() => toggleCellExpansion(`${paperId}:statistics`)}
                          showMoreLabel={showMoreLabel}
                          showLessLabel={showLessLabel}
                        >
                          {paper.statistics}
                        </CollapsibleCell>
                      </td>
                    )}
                    {visibleColumns.has("key_results") && (
                      <td>
                        <CollapsibleCell
                          isExpanded={expandedCells.has(`${paperId}:key_results`)}
                          onToggle={() => toggleCellExpansion(`${paperId}:key_results`)}
                          showMoreLabel={showMoreLabel}
                          showLessLabel={showLessLabel}
                        >
                          {paper.key_results}
                        </CollapsibleCell>
                      </td>
                    )}
                    {visibleColumns.has("rationale") && (
                      <td className="table-summary">
                        <CollapsibleCell
                          isExpanded={expandedCells.has(`${paperId}:rationale`)}
                          onToggle={() => toggleCellExpansion(`${paperId}:rationale`)}
                          showMoreLabel={showMoreLabel}
                          showLessLabel={showLessLabel}
                        >
                          <div className="whitespace-pre-wrap">{paper.rationale || "-"}</div>
                        </CollapsibleCell>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
